// ---------------------------------------------------------------------------
// regen_presets — catalogo generico de "atalhos de IA" para a regen de
// SubTopics da Documentation V3.
//
// Cada preset descreve UMA acao de refino que faz sentido em qualquer
// implementacao, qualquer Topic, qualquer SubTopic, em qualquer momento.
// Por isso o texto e' deliberadamente neutro:
//
//   - NAO mencionar nomes de clientes (ccu_argentina, ccu_chile, etc.)
//   - NAO mencionar termos especificos do metier (BOM, CMG, IQR, HL).
//   - NAO mencionar periodos especificos (jan/2025, ene/2026).
//   - NAO mencionar produtos/SKUs/plantas.
//
// O conteudo SUBSTANTIVO desses presets fica no proprio prompt do regen
// (regen_v3.md) e no glossario curado de cada implementacao — aqui sao
// apenas instrucoes de alto nivel para o LLM, que ele especializa
// usando o contexto que ja recebe.
//
// Quando uma das opcoes for clicada na UI, ``promptKey`` resolve via
// i18n para a frase a ser DEPOSITADA no campo de input do chat — o
// usuario pode editar antes de enviar.
//
// Para adicionar novo preset:
//   1. Adicionar o objeto aqui (categoria, id, icon, label/desc/prompt keys).
//   2. Adicionar as 3 strings i18n em `translation.json`
//      (ambos pt/es/en — fallback nao quebra, mas perde tradutibilidade).
//
// Reusabilidade: este modulo nao tem nenhuma dependencia. Pode ser
// importado por qualquer componente que precise listar/disparar presets.
// ---------------------------------------------------------------------------

/**
 * @typedef {Object} RegenPreset
 * @property {string} id           — identificador estavel (analytics / testing).
 * @property {string} category     — chave de categoria (i18n).
 * @property {string} icon         — caractere/emoji curto, mostrado no popover.
 * @property {string} labelKey     — i18n key do nome curto (botao).
 * @property {string} descKey      — i18n key da descricao (linha pequena).
 * @property {string} promptKey    — i18n key do texto que vai para o input do chat.
 * @property {boolean} [freshGeneration] — quando true, a proxima chamada
 *   deve ignorar o baseHtml (regra 17 do prompt v3 fica inerte). UI marca
 *   visualmente o modo "fresh" e zera o flag apos o envio.
 */

/** @type {RegenPreset[]} */
export const REGEN_PRESETS = [
  // ----- Recomecar (sempre primeira opcao do menu) -----
  // Categoria propria pra aparecer ANTES de "Conteudo" no popover.
  // ``freshGeneration`` flag e' lida pelo RegenPreviewModal para zerar
  // o baseHtml na proxima chamada — assim a LLM gera do zero, sem
  // tentar "preservar baseline" (regra 17 do prompt).
  {
    id: "fresh_start",
    category: "regen_preset.cat_restart",
    icon: "↻",
    labelKey: "regen_preset.fresh_start.label",
    descKey: "regen_preset.fresh_start.desc",
    promptKey: "regen_preset.fresh_start.prompt",
    freshGeneration: true,
  },
  // ----- Conteudo -----
  {
    id: "add_example",
    category: "regen_preset.cat_content",
    icon: "+",
    labelKey: "regen_preset.add_example.label",
    descKey: "regen_preset.add_example.desc",
    promptKey: "regen_preset.add_example.prompt",
  },
  {
    id: "add_formula",
    category: "regen_preset.cat_content",
    icon: "+",
    labelKey: "regen_preset.add_formula.label",
    descKey: "regen_preset.add_formula.desc",
    promptKey: "regen_preset.add_formula.prompt",
  },
  {
    id: "add_validation",
    category: "regen_preset.cat_content",
    icon: "+",
    labelKey: "regen_preset.add_validation.label",
    descKey: "regen_preset.add_validation.desc",
    promptKey: "regen_preset.add_validation.prompt",
  },
  // ----- Estilo -----
  {
    id: "shorten",
    category: "regen_preset.cat_style",
    icon: "✂",
    labelKey: "regen_preset.shorten.label",
    descKey: "regen_preset.shorten.desc",
    promptKey: "regen_preset.shorten.prompt",
  },
  {
    id: "densify",
    category: "regen_preset.cat_style",
    icon: "↑",
    labelKey: "regen_preset.densify.label",
    descKey: "regen_preset.densify.desc",
    promptKey: "regen_preset.densify.prompt",
  },
  {
    id: "soften_tone",
    category: "regen_preset.cat_style",
    icon: "~",
    labelKey: "regen_preset.soften_tone.label",
    descKey: "regen_preset.soften_tone.desc",
    promptKey: "regen_preset.soften_tone.prompt",
  },
  // ----- Estrutura -----
  {
    id: "reorder_by_gabarito",
    category: "regen_preset.cat_structure",
    icon: "↕",
    labelKey: "regen_preset.reorder_by_gabarito.label",
    descKey: "regen_preset.reorder_by_gabarito.desc",
    promptKey: "regen_preset.reorder_by_gabarito.prompt",
  },
  {
    id: "consolidate_formulas",
    category: "regen_preset.cat_structure",
    icon: "⊕",
    labelKey: "regen_preset.consolidate_formulas.label",
    descKey: "regen_preset.consolidate_formulas.desc",
    promptKey: "regen_preset.consolidate_formulas.prompt",
  },
  // ----- Validacao -----
  {
    id: "check_gabarito_coverage",
    category: "regen_preset.cat_validation",
    icon: "✓",
    labelKey: "regen_preset.check_gabarito_coverage.label",
    descKey: "regen_preset.check_gabarito_coverage.desc",
    promptKey: "regen_preset.check_gabarito_coverage.prompt",
  },
  {
    id: "scope_invasion",
    category: "regen_preset.cat_validation",
    icon: "⚠",
    labelKey: "regen_preset.scope_invasion.label",
    descKey: "regen_preset.scope_invasion.desc",
    promptKey: "regen_preset.scope_invasion.prompt",
  },
  {
    id: "banned_terms",
    category: "regen_preset.cat_validation",
    icon: "⊘",
    labelKey: "regen_preset.banned_terms.label",
    descKey: "regen_preset.banned_terms.desc",
    promptKey: "regen_preset.banned_terms.prompt",
  },
  // ----- Atualizacao -----
  {
    id: "refresh_period",
    category: "regen_preset.cat_update",
    icon: "↺",
    labelKey: "regen_preset.refresh_period.label",
    descKey: "regen_preset.refresh_period.desc",
    promptKey: "regen_preset.refresh_period.prompt",
  },
];

/**
 * Agrupa presets por categoria, mantendo a ordem original do array.
 * Util pelo popover (render por bloco).
 */
export function groupPresetsByCategory(presets = REGEN_PRESETS) {
  const groups = [];
  const seenCats = new Map();
  for (const p of presets) {
    if (!seenCats.has(p.category)) {
      seenCats.set(p.category, groups.length);
      groups.push({ category: p.category, items: [] });
    }
    groups[seenCats.get(p.category)].items.push(p);
  }
  return groups;
}
